import { useQuery } from '@tanstack/react-query';
import { useUserStore } from '@/store/userStore';
import { getRecentMembers } from '@/services/edgeFunctionWrappers/dashboardStatsWrapper';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserPlus, Clock, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export function RecentMembersWidget() {
  const { gymId } = useUserStore();

  const { data, isLoading } = useQuery({
    queryKey: ['recent-members', gymId],
    queryFn: () => getRecentMembers(gymId!),
    enabled: !!gymId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-primary" />
            Nouveaux inscrits
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-24">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const recentMembers = data || [];

  // Helper to get initials
  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-primary" />
          Nouveaux inscrits
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {recentMembers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Aucun nouvel inscrit récemment
          </p>
        ) : (
          <ul className="space-y-2">
            {recentMembers.map((member) => (
              <li 
                key={member.id} 
                className="flex items-center justify-between py-1.5 border-b last:border-0"
              >
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">
                      {getInitials(member.name)}
                    </span>
                  </div>
                  <span className="text-sm font-medium">{member.name}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  <span>
                    {formatDistanceToNow(new Date(member.joinedAt), { addSuffix: true, locale: fr })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
